import { Params, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { Roles } from '@models/roles.models';
import { companyContextGuard } from 'utils/company-context.guard';
import { PAGE_TITLE_SUFFIX, resolveTitleForParam } from 'utils/page-title-resolver';
import { RoleGuard } from 'utils/role.guard';

export enum APP_ROUTES {
  HOME = 'home',
  DASHBOARD = 'dashboard',
  LOGIN = 'login',
  LOGOUT = 'logout',
  ACCESS_DENIED = 'access-denied',
  CONNECTION_PROBLEM = 'connection-problem',
  REQUESTS = 'requests',
  ITEMS = 'items',
  SELECT_COMPANY = 'select-company',
  CLASSIFICATION = 'classification',
  EXPEDITE = 'expedite',
  DISPUTE = 'dispute',
  APP = 'app',
  SEARCH = 'search',
  AUDIT_HISTORY = 'audit-history',
  CREATE = 'create',
  VALIDATE = 'validate',
  SUBMITTED = 'submitted',
  INFORMATION_REQUESTS = 'information-requests',
}

export const REQUESTS_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_READER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const DISPUTE_REQUESTS_ROLES = [
  Roles.CLIENT_MANAGER,
  Roles.DISPUTE_MANAGER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const CREATE_REQUEST_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
];

export const MANAGE_REQUEST_DOCUMENTS_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const AUDIT_HISTORY_ROLES = []; // To be defined later

export const CREATE_DISPUTE_REQUEST_ROLES = [Roles.CLIENT_MANAGER, Roles.DISPUTE_MANAGER, Roles.INTERNAL_REQUESTER];

export const ADD_DISPUTE_ITEM_COMMENT_ROLES = [Roles.CLIENT_MANAGER, Roles.DISPUTE_MANAGER, Roles.INTERNAL_REQUESTER];

export const ADD_INFORMATION_REQUEST_COMMENT_ROLES = [
  Roles.TECHNICAL_ENGINEER,
  Roles.TECHNICAL_ENGINEER_MANAGER,
  Roles.INTERNAL_REQUESTER,
];

export const DASHBOARD_ROLES = [
  Roles.GOVERNANCE_MANAGER_EXTERNAL,
  Roles.CBAM_ANALYST,
  Roles.INTERNAL_CBAM_ANALYST,
  Roles.TRADE_ANALYST,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];
export const DASHBOARD_ACCESS = {
  [Roles.GOVERNANCE_MANAGER_EXTERNAL]: ['Classification'],
  [Roles.CBAM_ANALYST]: ['CBAM'],
  [Roles.INTERNAL_CBAM_ANALYST]: ['CBAM'],
  [Roles.TRADE_ANALYST]: ['Trade Analysis'],
  [Roles.INTERNAL_REQUESTER]: ['Classification'],
  [Roles.SUPPORT_ANALYST]: ['Classification', 'CBAM', 'Trade Analysis'],
};

export const INFORMATION_REQUESTS_ROLES = [
  Roles.TECHNICAL_ENGINEER_MANAGER,
  Roles.TECHNICAL_ENGINEER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const INFORMATION_REQUESTS_REASSIGNMENT_ROLES = [Roles.TECHNICAL_ENGINEER_MANAGER, Roles.TECHNICAL_ENGINEER];

export const SEARCH_ROLES = [
  Roles.CLIENT_USER,
  Roles.CLIENT_READER,
  Roles.CLIENT_MANAGER,
  Roles.CUSTOMS_BROKER,
  Roles.INTERNAL_REQUESTER,
  Roles.SUPPORT_ANALYST,
];

export const EXPEDITE_REQUESTS_ROLES = []; // To be defined later

export const FULL_DASHBOARD_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.DASHBOARD}`;
export const FULL_LOGIN_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.LOGIN}`;
export const FULL_LOGOUT_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.LOGOUT}`;
export const FULL_ACCESS_DENIED_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.ACCESS_DENIED}`;
export const FULL_CONNECTION_PROBLEM_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.CONNECTION_PROBLEM}`;
export const FULL_CLASSIFICATION_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}`;
export const FULL_EXPEDITE_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.EXPEDITE}`;
export const FULL_DISPUTE_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}`;
export const FULL_CREATE_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}`;
export const FULL_INFORMATION_REQUESTS_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.INFORMATION_REQUESTS}`;
export const FULL_SELECT_COMPANY_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.SELECT_COMPANY}`;
export const FULL_HOME_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.HOME}`;
export const FULL_SEARCH_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.SEARCH}`;
export const FULL_AUDIT_HISTORY_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.AUDIT_HISTORY}`;
export const FULL_CREATE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}`;
export const FULL_VALIDATE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}/${APP_ROUTES.VALIDATE}`;
export const FULL_SUBMITTED_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.CLASSIFICATION}/${APP_ROUTES.CREATE}/${APP_ROUTES.SUBMITTED}`;
export const FULL_VALIDATE_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}/${APP_ROUTES.VALIDATE}`;
export const FULL_SUBMITTED_DISPUTE_REQUEST_ROUTE = `/${APP_ROUTES.APP}/${APP_ROUTES.REQUESTS}/${APP_ROUTES.DISPUTE}/${APP_ROUTES.CREATE}/${APP_ROUTES.SUBMITTED}`;

export const ROUTES = {
  FULL_DASHBOARD_ROUTE,
  FULL_LOGIN_ROUTE,
  FULL_LOGOUT_ROUTE,
  FULL_CLASSIFICATION_REQUESTS_ROUTE,
  FULL_EXPEDITE_REQUESTS_ROUTE,
  FULL_DISPUTE_REQUESTS_ROUTE,
  FULL_INFORMATION_REQUESTS_ROUTE,
  FULL_HOME_ROUTE,
  FULL_SEARCH_ROUTE,
  FULL_AUDIT_HISTORY_ROUTE,
} as const;

const LOGIN_ROUTES: Routes = [
  {
    path: APP_ROUTES.LOGIN,
    title: 'Login' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: APP_ROUTES.LOGOUT,
    title: 'Logout' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/logout/logout.component').then(m => m.LogoutComponent),
  },
  {
    path: APP_ROUTES.CONNECTION_PROBLEM,
    title: 'Connection Problem' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/connection-problem/connection-problem.component').then(m => m.ConnectionProblemComponent),
  },
  {
    path: APP_ROUTES.ACCESS_DENIED,
    title: 'Access Denied' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
  },
] as const;

const HOME_ROUTES: Routes = [
  {
    path: APP_ROUTES.HOME,
    canActivate: [MsalGuard],
    data: { crumb: "What's New" },
    title: "What's New" + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/news/news.component').then(m => m.NewsComponent),
  },
] as const;

const SEARCH_ROUTES: Routes = [
  {
    path: APP_ROUTES.SEARCH,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: SEARCH_ROLES,
      crumb: 'Product Search',
    },
    title: 'Product Search' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/search/search.component').then(m => m.SearchComponent),
  },
] as const;

const AUDIT_HISTORY_ROUTES: Routes = [
  {
    path: APP_ROUTES.AUDIT_HISTORY,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: AUDIT_HISTORY_ROLES,
      crumb: 'Audit History',
    },
    title: 'Audit History' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/audit-history/audit-history.component').then(m => m.AuditHistoryComponent),
  },
] as const;

const USER_PROFILE_ROUTES: Routes = [
  {
    path: APP_ROUTES.SELECT_COMPANY,
    canActivate: [MsalGuard],
    title: 'Select Company' + PAGE_TITLE_SUFFIX,
    loadComponent: () =>
      import('./views/pages/select-company/select-company.component').then(m => m.SelectCompanyComponent),
  },
];

const ANONYMOUS_ROUTES: Routes = [
  {
    path: '',
    data: { hidePrivacyNoticeLink: true },
    loadComponent: () => import('./views/layout/anonymous/anonymous.component').then(m => m.AnonymousComponent),
    children: [...LOGIN_ROUTES, ...USER_PROFILE_ROUTES],
  },
] as const;

const DASHBOARD_ROUTES: Routes = [
  {
    path: APP_ROUTES.DASHBOARD,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      roles: DASHBOARD_ROLES,
      crumb: 'Dashboard',
    },
    title: 'Dashboard' + PAGE_TITLE_SUFFIX,
    loadComponent: () => import('./views/pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
  },
] as const;

export const ITEMS_ROUTES: Routes = [
  {
    path: `:itemEntityId/:itemId`,
    data: { crumb: (params: Params) => `${params['itemId']}` },
    title: resolveTitleForParam('itemId'),
    loadComponent: () => import('./views/pages/item-details/item-details.component').then(m => m.ItemDetailsComponent),
  },
];

export const DISPUTE_ITEMS_ROUTES: Routes = [
  {
    path: `:disputeItemEntityId/:disputeItemId`,
    data: { crumb: (params: Params) => `${params['disputeItemId']}` },
    title: resolveTitleForParam('disputeItemId'),
    loadComponent: () =>
      import('./views/pages/dispute-item-details/dispute-item-details.component').then(
        m => m.DisputeItemDetailsComponent
      ),
  },
];

const REQUESTS_ROUTES: Routes = [
  {
    path: APP_ROUTES.REQUESTS,
    redirectTo: FULL_HOME_ROUTE,
    pathMatch: 'full',
  },
  {
    path: APP_ROUTES.REQUESTS,
    canActivate: [MsalGuard, companyContextGuard],
    children: [
      {
        path: APP_ROUTES.CLASSIFICATION,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: {
          crumb: 'Classification Requests',
          roles: REQUESTS_ROLES,
        },
        title: 'Classification Requests' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: ``,
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/requests/classification/classification.component').then(
                m => m.ClassificationComponent
              ),
          },
          {
            path: APP_ROUTES.CREATE,
            canActivate: [RoleGuard],
            data: {
              crumb: 'Create Classification Request',
              roles: CREATE_REQUEST_ROLES,
            },
            title: 'Create Classification Request' + PAGE_TITLE_SUFFIX,
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/requests/classification/create-request/create-request.component').then(
                    m => m.CreateRequestComponent
                  ),
              },
              {
                path: 'validate/:requestValidationSubmissionId',
                data: { crumb: 'Data Validation' },
                title: 'Data Validation' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/requests/classification/validate-request/validate-request.component').then(
                    m => m.ValidateRequestComponent
                  ),
              },
              {
                path: 'submitted/:requestValidationSubmissionId/:requestEntityId/:requestId',
                data: { crumb: 'Submitted Successfully' },
                title: 'Submitted Successfully' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/requests/submitted-request/submitted-request.component').then(
                    m => m.SubmittedRequestComponent
                  ),
              },
            ],
          },
          {
            path: `:requestEntityId/:requestId`,
            data: { crumb: (params: Params) => `${params['requestId']}` },
            title: resolveTitleForParam('requestId'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/request-details/request-details.component').then(
                    m => m.RequestDetailsComponent
                  ),
              },

              ...ITEMS_ROUTES,
            ],
          },
        ],
      },

      {
        path: APP_ROUTES.EXPEDITE,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: { crumb: 'Expedite Requests', roles: EXPEDITE_REQUESTS_ROLES },
        title: 'Expedite Requests' + PAGE_TITLE_SUFFIX,
        loadComponent: () =>
          import('./views/pages/requests/expedite/expedite.component').then(m => m.ExpediteComponent),
      },

      {
        path: APP_ROUTES.DISPUTE,
        canActivate: [MsalGuard, companyContextGuard, RoleGuard],
        data: { crumb: 'Dispute Requests', roles: DISPUTE_REQUESTS_ROLES },
        title: 'Dispute Requests' + PAGE_TITLE_SUFFIX,
        children: [
          {
            path: ``,
            data: { crumb: undefined },
            loadComponent: () =>
              import('./views/pages/dispute-requests/dispute-requests.component').then(m => m.DisputeRequestsComponent),
          },
          {
            path: APP_ROUTES.CREATE,
            canActivate: [RoleGuard],
            data: {
              crumb: 'Create Dispute Request',
              roles: CREATE_DISPUTE_REQUEST_ROLES,
            },
            title: 'Create Dispute Request' + PAGE_TITLE_SUFFIX,
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/dispute-requests/create-dispute-request/create-dispute-request.component').then(
                    m => m.CreateDisputeRequestComponent
                  ),
              },
              {
                path: 'validate/:disputeRequestValidationSubmissionId',
                data: { crumb: 'Data Validation' },
                title: 'Data Validation' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import(
                    './views/pages/dispute-requests/validate-dispute-request/validate-dispute-request.component'
                  ).then(m => m.ValidateDisputeRequestComponent),
              },
              {
                path: 'submitted/:disputeRequestValidationSubmissionId/:disputeRequestEntityId/:disputeRequestId',
                data: { crumb: 'Submitted Successfully' },
                title: 'Submitted Successfully' + PAGE_TITLE_SUFFIX,
                loadComponent: () =>
                  import('./views/pages/dispute-requests/submit-dispute-request/submit-dispute-request.component').then(
                    m => m.SubmitDisputeRequestComponent
                  ),
              },
            ],
          },
          {
            path: `:disputeRequestEntityId/:disputeRequestId`,
            data: { crumb: (params: Params) => `${params['disputeRequestId']}` },
            title: resolveTitleForParam('disputeRequestId'),
            children: [
              {
                path: '',
                data: { crumb: undefined },
                loadComponent: () =>
                  import('./views/pages/dispute-request-details/dispute-request-details.component').then(
                    m => m.DisputeRequestDetailsComponent
                  ),
              },

              {
                path: 'items',
                data: { crumb: undefined },
                children: [...ITEMS_ROUTES],
              },
              ...DISPUTE_ITEMS_ROUTES,
            ],
          },
        ],
      },
    ],
  },
  { path: '', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
] as const;

export const INFORMATION_REQUESTS_ROUTES: Routes = [
  {
    path: APP_ROUTES.INFORMATION_REQUESTS,
    canActivate: [MsalGuard, companyContextGuard, RoleGuard],
    data: {
      crumb: 'Information Requests',
      roles: INFORMATION_REQUESTS_ROLES,
    },
    title: 'Information Requests' + PAGE_TITLE_SUFFIX,
    children: [
      {
        path: '',
        data: { crumb: undefined },
        loadComponent: () =>
          import('./views/pages/information-requests/information-requests.component').then(
            m => m.InformationRequestsComponent
          ),
      },
      {
        path: ':informationRequestEntityId/:informationRequestId',
        data: { crumb: (params: Params) => `${params['informationRequestId']}` },
        title: resolveTitleForParam('informationRequestId'),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./views/pages/information-request-details/information-request-details.component').then(
                m => m.InformationRequestDetailsComponent
              ),
          },
          ...ITEMS_ROUTES,
        ],
      },
      {
        path: 'items',
        data: { crumb: undefined },
        children: [...ITEMS_ROUTES],
      },
    ],
  },
];

export const MAIN_ROUTES: Routes = [
  {
    path: '',
    canActivate: [MsalGuard, companyContextGuard],
    loadComponent: () => import('./views/layout/main/main.component').then(m => m.MainComponent),
    data: { crumb: 'Home' },
    children: [
      ...DASHBOARD_ROUTES,
      ...REQUESTS_ROUTES,
      ...INFORMATION_REQUESTS_ROUTES,
      ...HOME_ROUTES,
      ...SEARCH_ROUTES,
      ...AUDIT_HISTORY_ROUTES,
    ],
  },
] as const;

export const routes: Routes = [
  {
    path: APP_ROUTES.APP,
    children: [
      { path: '', redirectTo: APP_ROUTES.HOME, pathMatch: 'full' },
      ...ANONYMOUS_ROUTES,
      ...MAIN_ROUTES,
      { path: '**', redirectTo: APP_ROUTES.HOME, pathMatch: 'full' },
    ],
  },
  { path: '', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
  { path: '**', redirectTo: FULL_HOME_ROUTE, pathMatch: 'full' },
] as const;
